import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

const tabs = [
  { id: "overview", label: "Overview", icon: "fa-heart-pulse" },
  { id: "capture", label: "Capture", icon: "fa-pen-nib" },
  { id: "signals", label: "Signals", icon: "fa-wave-square" },
  { id: "insights", label: "Insights", icon: "fa-brain" },
  { id: "actions", label: "Actions", icon: "fa-hand-holding-heart" },
];

export default function WISHome() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };    

  const [activeTab, setActiveTab] = useState("overview");
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (activeTab === "capture") {
      setStep(1);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-4">
      {/* Header */}
      {/* <header className="mb-4">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <i className="fa-solid fa-seedling text-green-400"></i>
          WIS – Well-Being Insights System
        </h1>
        <p className="text-sm text-white mt-1">
          Early awareness • Ethical • Non-diagnostic
        </p>
      </header> */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 mb-2 flex items-center justify-between">
            <div className="flex flex-col">
                <div className="text-lg lg:text-xl font-bold text-yellow-200">
                     <i className="fa-solid fa-seedling text-green-400"></i>WIS – Well-Being Insights System
                </div>
                <div className="text-[11px] text-white">Early awareness • Ethical • Non-diagnostic</div>
            </div>

            <div className="hidden md:flex items-center gap-3">
                {isLoggedIn ? (
                    <>
                    <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
                    <button
                        onClick={handleLogout}
                        className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                        title="Logout"
                    >
                        ⏻
                    </button>
                    </>
                ) : (
                    <>
                    <button
                        onClick={() => navigate("/login")}
                        className="w-18 px-2 py-1 rounded-full border border-gray-700 text-sm text-white hover:bg-gray-800"
                    >
                        🔐 
                    </button>
                    </>
                )}
            </div>
            <button
                onClick={() => setIsMobileMenuOpen((s) => !s)}
                className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
                aria-label="Toggle menu"
                >
                {isMobileMenuOpen ? "×" : "☰"}
            </button>
        </header>

        {isMobileMenuOpen && (
            <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
            {isLoggedIn ? (
                <div className="flex flex-col items-center gap-3">
                <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                    <div className="flex gap-3">
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                    >
                        ⏻
                    </button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-center gap-6">
                <Link to="/login" className="text-teal-300">🔐</Link>
                </div>
            )}
            </div>
        )}      

      <div className="min-h-[75vh]">
        <div className="grid grid-cols-5 gap-2 mb-6">
            {tabs.map((tab) => (
            <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 rounded-lg text-xs flex flex-col items-center justify-center gap-1
                ${
                    activeTab === tab.id
                    ? "bg-gray-800 text-white"
                    : "bg-gray-900 text-white"
                }`}
            >
                <i className={`fa-solid ${tab.icon}`}></i>
                <span className="hidden sm:block">{tab.label}</span>
            </button>
            ))}
        </div>

        <div className="bg-gray-900 rounded-xl p-4">
            {activeTab === "overview" && <Overview />}
            {activeTab === "capture" && (
            <Capture step={step} setStep={setStep} />
            )}
            {activeTab === "signals" && <Signals />}
            {activeTab === "insights" && <Insights />}
            {activeTab === "actions" && <Actions />}
        </div>
      </div>      

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/about-us" className="hover:text-teal-300">About</Link>
                <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
        </div>
       </footer>      
    </div>
  );
}

/* ----------------------- TAB COMPONENTS ----------------------- */

function Overview() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-yellow-200">
        🌱 What is WIS?
      </h2>
      <p className="text-sm text-white">
        WIS is a <b>student well-being early awareness system</b>. It does not
        diagnose mental health conditions. It observes <b>changes over time</b>
        using multiple soft signals.
      </p>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <InfoCard icon="fa-pen" title="Handwriting Trends" />
        <InfoCard icon="fa-calendar-check" title="Attendance Patterns" />
        <InfoCard icon="fa-chart-line" title="Academic Change" />
        <InfoCard icon="fa-comments" title="Self Check-ins" />
      </div>

      <div className="bg-black border border-gray-700 rounded-lg p-3 text-xs">
        ⚠️ Ethical Note:  
        <ul className="list-disc pl-4 mt-2 space-y-1  [&>li]:text-white">
          <li>No diagnosis</li>
          <li>No labeling</li>
          <li>Consent-driven</li>
          <li>Human-in-the-loop</li>
        </ul>
      </div>
    </div>
  );
}

function Capture({ step, setStep }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-yellow-200">✍️ Data Capture (Simulated)</h2>

      {/* Step Progress */}
      <div className="flex items-center gap-2 text-xs">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`flex-1 h-1 rounded ${
              step >= s ? "bg-green-500" : "bg-gray-700"
            }`}
          ></div>
        ))}
      </div>

      {step === 1 && (
        <StepCard
          title="Step 1: Handwriting Sample"
          desc="Student uploads or writes a short paragraph (baseline or weekly)."
          action={() => setStep(2)}
          icon="fa-file-signature"
          btn="Simulate Upload"
        />
      )}

      {step === 2 && (
        <StepCard
          title="Step 2: Context Check"
          desc="Student selects mood, sleep quality, and stress level."
          action={() => setStep(3)}
          icon="fa-sliders"
          btn="Simulate Check-in"
        />
      )}

      {step === 3 && (
        <StepCard
          title="Step 3: Secure Storage"
          desc="Data is anonymized and stored for trend comparison."
          action={() => setStep(1)}
          icon="fa-lock"
          btn="Finish Cycle"
        />
      )}
    </div>
  );
}

function Signals() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-yellow-200">📊 Observed Signals</h2>

      <Signal
        label="Handwriting Consistency"
        value="Slight Variation"
        color="yellow"
      />
      <Signal
        label="Writing Speed"
        value="Slower than baseline"
        color="orange"
      />
      <Signal
        label="Pressure Pattern"
        value="Normal"
        color="green"
      />
      <Signal
        label="Self-reported Stress"
        value="Moderate"
        color="orange"
      />

      <p className="text-xs text-white">
        Signals are compared <b>only with student’s own past data</b>.
      </p>
    </div>
  );
}

function Insights() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-yellow-200">🧠 Generated Insights</h2>

      <div className="bg-black border border-gray-700 rounded-lg p-3 text-sm">
        <p className="text-white">
          📌 Observation:
        </p>
        <p className="mt-1 text-white">
          Recent handwriting samples show increased variability and slower
          writing speed compared to last 3 weeks.
        </p>
      </div>

      <div className="bg-black border border-yellow-600 rounded-lg p-3 text-sm">
        <p className="text-yellow-400 font-semibold">
          ⚠️ Soft Alert
        </p>
        <p className="text-white mt-1">
          Pattern may indicate increased academic or emotional pressure.
        </p>
      </div>

      <p className="text-xs text-white">
        This is NOT a diagnosis. It is a well-being trend flag.
      </p>
    </div>
  );
}

function Actions() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-yellow-200">🤝 Recommended Actions</h2>

      <ActionCard
        icon="fa-comments"
        title="Gentle Check-in"
        desc="Encourage a supportive conversation with the student."
      />
      <ActionCard
        icon="fa-person-chalkboard"
        title="Academic Support"
        desc="Review workload or upcoming assessments."
      />
      <ActionCard
        icon="fa-user-shield"
        title="Counselor Referral"
        desc="Optional and consent-based if trends continue."
      />

      <div className="bg-black border border-gray-700 rounded-lg p-3 text-xs text-white">
        All actions require <b>human review and consent</b>.
      </div>
    </div>
  );
}

/* ----------------------- SMALL UI COMPONENTS ----------------------- */

const InfoCard = ({ icon, title }) => (
  <div className="bg-black border border-gray-700 rounded-lg p-3 flex items-center gap-2">
    <i className={`fa-solid ${icon} text-blue-400`}></i>
    <span>{title}</span>
  </div>
);

const StepCard = ({ title, desc, action, icon, btn }) => (
  <div className="bg-black border border-gray-700 rounded-lg p-4 space-y-3">
    <h3 className="font-semibold flex items-center gap-2">
      <i className={`fa-solid ${icon} text-purple-400`}></i>
      {title}
    </h3>
    <p className="text-sm text-white">{desc}</p>
    <button
      onClick={action}
      className="w-full py-2 bg-gray-800 rounded-lg text-sm hover:bg-gray-700"
    >
      {btn}
    </button>
  </div>
);

const Signal = ({ label, value, color }) => (
  <div className="flex justify-between text-sm bg-black border border-gray-700 rounded-lg p-3">
    <span>{label}</span>
    <span className={`text-${color}-400`}>{value}</span>
  </div>
);

const ActionCard = ({ icon, title, desc }) => (
  <div className="bg-black border border-gray-700 rounded-lg p-3 flex gap-3">
    <i className={`fa-solid ${icon} text-green-400 mt-1`}></i>
    <div>
      <p className="font-semibold text-yellow-200">{title}</p>
      <p className="text-sm text-white">{desc}</p>
    </div>
  </div>
);
